import React, { useState, useMemo } from 'react';
import { CompleteKundali, Language } from '../types/kundali';
import {
  NumerologySystem,
  BrandPurpose,
  CandidateEvaluation,
  ComparisonResult,
  compareNameCandidates,
  evaluateNameCandidate,
  evaluateNameChange,
  getRegistrationMuhurtaRecommendations,
  getCompoundNumberData
} from '../utils/nameNumerologyEngine';
import {
  Sparkles,
  Trophy,
  Award,
  Globe,
  Smartphone,
  Briefcase,
  User,
  Star,
  CheckCircle2,
  AlertCircle,
  HelpCircle,
  TrendingUp,
  RotateCcw,
  Plus,
  Trash2,
  ArrowRight,
  ShieldCheck,
  Zap,
  Info,
  Calendar,
  Layers,
  Search,
  ExternalLink,
  Copy,
  Check
} from 'lucide-react';

interface NameBrandComparisonEngineProps {
  kundali?: CompleteKundali | null;
  lang: Language;
  defaultCandidates?: string[];
  defaultPurpose?: BrandPurpose;
}

export const NameBrandComparisonEngine: React.FC<NameBrandComparisonEngineProps> = ({
  kundali,
  lang,
  defaultCandidates = ['Vedanga Jyotisha', 'Vedanga Jyotisham', 'Vedang Jyotish', 'Vedang Jyotisham'],
  defaultPurpose = 'ASTROLOGY_APP'
}) => {
  const isHi = lang === 'hi';

  // System selection: Chaldean (Default) vs Pythagorean
  const [system, setSystem] = useState<NumerologySystem>('CHALDEAN');

  // Brand Purpose
  const [purpose, setPurpose] = useState<BrandPurpose>(defaultPurpose);

  // Active view tab: 'comparison' | 'deep-dive' | 'name-change' | 'muhurta'
  const [activeTab, setActiveTab] = useState<'comparison' | 'deep-dive' | 'name-change' | 'muhurta'>('comparison');

  // Candidate names list
  const [candidates, setCandidates] = useState<string[]>(defaultCandidates);
  const [newCandidateInput, setNewCandidateInput] = useState<string>('');

  // Selected candidate for deep dive
  const [selectedCandidateIndex, setSelectedCandidateIndex] = useState<number>(0);

  // Name change state
  const [currentName, setCurrentName] = useState<string>('Vedang Jyotish');
  const [proposedName, setProposedName] = useState<string>('Vedanga Jyotisha');

  // Copied feedback state
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Optional owner details derived from Kundali
  const ownerBirthNumber = useMemo(() => {
    if (!kundali?.birthDetails.date) return undefined;
    const day = parseInt(kundali.birthDetails.date.split('-')[2], 10);
    if (isNaN(day)) return undefined;
    let sum = day;
    while (sum > 9) {
      sum = sum.toString().split('').reduce((acc, d) => acc + parseInt(d, 10), 0);
    }
    return sum;
  }, [kundali]);

  const ownerDestinyNumber = useMemo(() => {
    if (!kundali?.birthDetails.date) return undefined;
    const raw = kundali.birthDetails.date.replace(/\D/g, '');
    let sum = raw.split('').reduce((acc, d) => acc + parseInt(d, 10), 0);
    while (sum > 9) {
      sum = sum.toString().split('').reduce((acc, d) => acc + parseInt(d, 10), 0);
    }
    return sum;
  }, [kundali]);

  // Master comparison evaluation
  const comparisonResult: ComparisonResult = useMemo(() => {
    return compareNameCandidates(
      candidates,
      system,
      purpose,
      kundali,
      ownerBirthNumber,
      ownerDestinyNumber
    );
  }, [candidates, system, purpose, kundali, ownerBirthNumber, ownerDestinyNumber]);

  // Name Change assessment
  const nameChangeResult = useMemo(() => {
    return evaluateNameChange(currentName, proposedName, system, purpose, kundali);
  }, [currentName, proposedName, system, purpose, kundali]);

  // Muhurta for best overall
  const bestCandidateMuhurta = useMemo(() => {
    if (!comparisonResult.bestOverall) return null;
    return getRegistrationMuhurtaRecommendations(comparisonResult.bestOverall);
  }, [comparisonResult.bestOverall]);

  // Handler to add new candidate
  const handleAddCandidate = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const trimmed = newCandidateInput.trim();
    if (!trimmed) return;
    if (candidates.includes(trimmed)) return;
    setCandidates([...candidates, trimmed]);
    setNewCandidateInput('');
  };

  // Handler to remove candidate
  const handleRemoveCandidate = (indexToRemove: number) => {
    if (candidates.length <= 1) return;
    const updated = candidates.filter((_, idx) => idx !== indexToRemove);
    setCandidates(updated);
    if (selectedCandidateIndex >= updated.length) {
      setSelectedCandidateIndex(0);
    }
  };

  // Preset loaders
  const loadPresetGroup = (groupType: 'vedanga' | 'apps' | 'business' | 'personal') => {
    if (groupType === 'vedanga') {
      setCandidates(['Vedanga Jyotisha', 'Vedanga Jyotisham', 'Vedang Jyotish', 'Vedang Jyotisham']);
      setPurpose('ASTROLOGY_APP');
    } else if (groupType === 'apps') {
      setCandidates(['Vedanga AI', 'AstroVeda Kundali', 'VedaJyotish App', 'Jyotisha Studio']);
      setPurpose('ASTROLOGY_APP');
    } else if (groupType === 'business') {
      setCandidates(['Vedanga Astrological Services', 'Jyotishya Kendra', 'Vedic Horizon Consulting', 'Divya Jyoti Pratishthan']);
      setPurpose('BUSINESS');
    } else if (groupType === 'personal') {
      const base = kundali?.birthDetails.name || 'Aditya Sharma';
      setCandidates([base, `A${base}`, `${base.split(' ')[0]} R ${base.split(' ')[1] || ''}`.trim(), `${base.split(' ')[0]} Kumar`]);
      setPurpose('PERSONAL_BRAND');
    }
  };

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  // Render Stars
  const renderStars = (rating: number) => {
    return (
      <div className="flex items-center gap-0.5 text-amber-500">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-3.5 h-3.5 ${
              star <= rating ? 'fill-amber-400 text-amber-500' : 'text-astro-muted'
            }`}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Top Controls Card */}
      <div className="bg-astro-bg border border-astro-border rounded-2xl p-5 shadow-sm space-y-4">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 border-b border-astro-border pb-4">
          <div>
            <div className="flex items-center gap-2">
              <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold tracking-wider uppercase bg-astro-primary/10 text-astro-primary">
                {isHi ? 'नाम व ब्रांड अंक विश्लेषण' : 'Name & Brand Comparison Engine'}
              </span>
              <span className="px-2 py-0.5 rounded-md text-[10px] font-semibold bg-astro-secondary-surface text-astro-secondary border border-[#E2DED5]">
                {system === 'CHALDEAN' ? 'Chaldean System (Default)' : 'Pythagorean System'}
              </span>
            </div>
            <h3 className="text-xl font-bold font-serif text-astro-primary mt-1">
              {isHi ? 'नाम, ब्रांड, ऐप एवं डोमेन अंक तुलना' : 'Brand, App & Domain Name Evaluator'}
            </h3>
            <p className="text-xs text-astro-secondary-text mt-0.5">
              {isHi
                ? 'चैल्डियन एवं पाइथागोरस पद्धति द्वारा एकाधिक नामों का तुलनात्मक मूल्यांकन, संयुक्त अंक (Compound Number), ध्वनि प्रवाह एवं कुंडली तालमेल।'
                : 'Evaluate multi-candidate names, compound vibration matrices, phonetic flow, domain parsing, and astrological Kundali synergy.'}
            </p>
          </div>

          {/* Config Controls: System & Purpose */}
          <div className="flex flex-wrap items-center gap-3">
            {/* System Selector */}
            <div className="flex items-center bg-astro-card p-1 rounded-xl border border-astro-border text-xs">
              <button
                type="button"
                onClick={() => setSystem('CHALDEAN')}
                className={`px-3 py-1.5 rounded-lg font-semibold transition-all ${
                  system === 'CHALDEAN'
                    ? 'bg-astro-primary text-astro-bg shadow-sm'
                    : 'text-astro-secondary-text hover:text-astro-primary'
                }`}
              >
                {isHi ? 'चैल्डियन (Chaldean)' : 'Chaldean'}
              </button>
              <button
                type="button"
                onClick={() => setSystem('PYTHAGOREAN')}
                className={`px-3 py-1.5 rounded-lg font-semibold transition-all ${
                  system === 'PYTHAGOREAN'
                    ? 'bg-astro-primary text-astro-bg shadow-sm'
                    : 'text-astro-secondary-text hover:text-astro-primary'
                }`}
              >
                {isHi ? 'पाइथागोरस (Pythagorean)' : 'Pythagorean'}
              </button>
            </div>

            {/* Purpose Selector */}
            <div className="flex items-center gap-1.5">
              <label htmlFor="brand-purpose-select" className="text-xs font-semibold text-astro-secondary-text whitespace-nowrap">
                {isHi ? 'उद्देश्य:' : 'Purpose:'}
              </label>
              <select
                id="brand-purpose-select"
                aria-label={isHi ? 'ब्रांड का उद्देश्य चुनें' : 'Select brand purpose'}
                value={purpose}
                onChange={(e) => setPurpose(e.target.value as BrandPurpose)}
                className="text-xs font-semibold bg-astro-card border border-astro-border text-astro-primary rounded-xl px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-astro-primary"
              >
                <option value="ASTROLOGY_APP">{isHi ? '📱 ज्योतिष ऐप (Astrology App)' : '📱 Astrology App'}</option>
                <option value="ASTROLOGY_PRACTICE">{isHi ? '🔮 ज्योतिष परामर्श (Astrology Practice)' : '🔮 Astrology Practice'}</option>
                <option value="PERSONAL_BRAND">{isHi ? '👤 व्यक्तिगत ब्रांड (Personal Brand)' : '👤 Personal Brand'}</option>
                <option value="BUSINESS">{isHi ? '💼 व्यापार / उद्यम (Business)' : '💼 Business / Enterprise'}</option>
                <option value="PRODUCT">{isHi ? '📦 उत्पाद / सेवा (Product)' : '📦 Product'}</option>
                <option value="WEBSITE">{isHi ? '🌐 वेबसाइट (Website)' : '🌐 Website'}</option>
                <option value="DOMAIN">{isHi ? '🔗 डोमेन नाम (Domain Name)' : '🔗 Domain Name'}</option>
                <option value="COMPANY">{isHi ? '🏢 कंपनी (Company)' : '🏢 Company'}</option>
                <option value="OTHER">{isHi ? '✨ अन्य (Other)' : '✨ Other'}</option>
              </select>
            </div>
          </div>
        </div>

        {/* Candidate Input & Presets Row */}
        <div className="space-y-3">
          {/* Preset Buttons */}
          <div className="flex items-center gap-2 overflow-x-auto pb-1 text-xs">
            <span className="text-[11px] font-bold text-astro-secondary-text uppercase tracking-wider whitespace-nowrap">
              {isHi ? 'त्वरित प्रीसेट:' : 'Quick Presets:'}
            </span>
            <button
              type="button"
              onClick={() => loadPresetGroup('vedanga')}
              className="px-2.5 py-1 rounded-lg bg-astro-card hover:bg-astro-border text-astro-primary border border-astro-border whitespace-nowrap font-medium transition-colors"
            >
              🪐 Vedanga Jyotisha Candidates
            </button>
            <button
              type="button"
              onClick={() => loadPresetGroup('apps')}
              className="px-2.5 py-1 rounded-lg bg-astro-card hover:bg-astro-border text-astro-primary border border-astro-border whitespace-nowrap font-medium transition-colors"
            >
              📱 Astrology App Names
            </button>
            <button
              type="button"
              onClick={() => loadPresetGroup('business')}
              className="px-2.5 py-1 rounded-lg bg-astro-card hover:bg-astro-border text-astro-primary border border-astro-border whitespace-nowrap font-medium transition-colors"
            >
              💼 Practice & Consulting
            </button>
            <button
              type="button"
              onClick={() => loadPresetGroup('personal')}
              className="px-2.5 py-1 rounded-lg bg-astro-card hover:bg-astro-border text-astro-primary border border-astro-border whitespace-nowrap font-medium transition-colors"
            >
              👤 Personal Name Variations
            </button>
          </div>

          {/* Add Candidate Form */}
          <form onSubmit={handleAddCandidate} className="flex items-center gap-2">
            <div className="relative flex-1">
              <input
                type="text"
                value={newCandidateInput}
                onChange={(e) => setNewCandidateInput(e.target.value)}
                placeholder={isHi ? 'नया नाम या डोमेन दर्ज करें (उदा. Vedanga Jyotisha, vedangajyotisha.com)...' : 'Enter candidate name or domain (e.g. Vedanga Jyotisha, vedangajyotisha.com)...'}
                className="w-full bg-astro-card border border-astro-border rounded-xl px-4 py-2 text-xs font-medium text-astro-primary placeholder:text-astro-secondary-text/60 focus:outline-none focus:ring-1 focus:ring-astro-primary"
              />
            </div>
            <button
              type="submit"
              disabled={!newCandidateInput.trim()}
              className="px-4 py-2 bg-astro-primary hover:bg-[#1F5F5E] disabled:opacity-50 text-astro-bg rounded-xl text-xs font-bold transition-colors flex items-center gap-1.5 shadow-sm shrink-0"
            >
              <Plus className="w-4 h-4" />
              <span>{isHi ? 'नाम जोड़ें' : 'Add Candidate'}</span>
            </button>
          </form>

          {/* Candidate Chips */}
          <div className="flex flex-wrap items-center gap-2 pt-1">
            {candidates.map((cand, idx) => (
              <div
                key={idx}
                className="flex items-center gap-1.5 bg-astro-card border border-astro-border px-3 py-1.5 rounded-xl text-xs font-semibold text-astro-primary shadow-2xs group"
              >
                <span>{cand}</span>
                {candidates.length > 1 && (
                  <button
                    type="button"
                    onClick={() => handleRemoveCandidate(idx)}
                    className="text-astro-secondary-text hover:text-red-600 transition-colors p-0.5 rounded"
                    title={isHi ? 'हटाएं' : 'Remove'}
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* View Mode Navigation Tabs */}
        <div className="flex items-center gap-2 pt-3 border-t border-astro-border overflow-x-auto">
          {[
            { id: 'comparison', label: isHi ? 'तुलनात्मक तालिका' : 'Comparison Matrix', icon: Layers },
            { id: 'deep-dive', label: isHi ? 'विस्तृत अंक विश्लेषण' : 'Candidate Deep-Dive', icon: Sparkles },
            { id: 'name-change', label: isHi ? 'नाम परिवर्तन मूल्यांकन' : 'Name Change Evaluation', icon: TrendingUp },
            { id: 'muhurta', label: isHi ? 'शुभ पंजीकरण मुहूर्त' : 'Registration Muhurta', icon: Calendar }
          ].map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold whitespace-nowrap transition-all ${
                  isActive
                    ? 'bg-astro-primary text-astro-bg shadow-sm'
                    : 'bg-astro-card hover:bg-astro-border text-astro-secondary-text'
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* VIEW 1: COMPARISON MATRIX */}
      {activeTab === 'comparison' && (
        <div className="space-y-6">
          {/* Winner Podium Banner */}
          {comparisonResult.bestOverall && (
            <div className="bg-gradient-to-r from-amber-500/10 via-amber-400/15 to-amber-500/10 border-2 border-amber-400/40 rounded-2xl p-5 shadow-sm relative overflow-hidden">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-start gap-3.5">
                  <div className="w-12 h-12 rounded-2xl bg-astro-secondary-surface0 text-white flex items-center justify-center shadow-md shrink-0">
                    <Trophy className="w-6 h-6" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="px-2.5 py-0.5 rounded-full text-[10px] font-extrabold uppercase tracking-wider bg-astro-secondary-surface0 text-white shadow-2xs">
                        🏆 {isHi ? 'सर्वोत्तम अनुशंसित नाम' : 'BEST OVERALL CANDIDATE'}
                      </span>
                      <span className="text-xs font-bold text-astro-secondary">
                        Score: {comparisonResult.bestOverall.overallScore}/100
                      </span>
                    </div>
                    <h4 className="text-2xl font-bold font-serif text-astro-primary mt-1">
                      {comparisonResult.bestOverall.brandName}
                    </h4>
                    <p className="text-xs text-astro-text mt-1 max-w-3xl leading-relaxed">
                      {isHi ? comparisonResult.whyBestHi : comparisonResult.whyBest}
                    </p>
                  </div>
                </div>

                <div className="flex sm:flex-col items-center sm:items-end justify-between sm:justify-center gap-2 shrink-0 border-t sm:border-t-0 border-[#E2DED5]/40 pt-3 sm:pt-0">
                  <div className="text-right">
                    <div className="text-[10px] uppercase font-bold text-astro-secondary-text">
                      {isHi ? 'संयुक्त अंक' : 'Compound Vibration'}
                    </div>
                    <div className="text-lg font-black text-astro-primary">
                      {comparisonResult.bestOverall.chaldeanCompound} → {comparisonResult.bestOverall.chaldeanSingle}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedCandidateIndex(0);
                      setActiveTab('deep-dive');
                    }}
                    className="px-3 py-1.5 bg-astro-primary text-astro-bg hover:bg-[#1F5F5E] rounded-xl text-xs font-semibold transition-colors flex items-center gap-1 shadow-2xs"
                  >
                    <span>{isHi ? 'पूर्ण विवरण देखें' : 'View Deep Analysis'}</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Master Comparison Table */}
          <div className="bg-astro-bg border border-astro-border rounded-2xl overflow-hidden shadow-sm">
            <div className="p-4 bg-astro-card border-b border-astro-border flex items-center justify-between">
              <div>
                <h4 className="text-sm font-bold text-astro-primary">
                  {isHi ? 'उम्मीदवारों की तुलनात्मक तालिका' : 'Candidate Evaluation Matrix'}
                </h4>
                <p className="text-[11px] text-astro-secondary-text">
                  {isHi
                    ? 'सभी उम्मीदवारों का एकसमान गणितीय पद्धति द्वारा निष्पक्ष व पारदर्शी विश्लेषण।'
                    : 'Consistent standardized mathematical evaluation across all candidates.'}
                </p>
              </div>
              <span className="text-xs font-semibold text-astro-primary">
                {candidates.length} {isHi ? 'नाम जांचे गए' : 'Candidates'}
              </span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse text-xs">
                <thead>
                  <tr className="border-b border-astro-border bg-astro-bg text-astro-secondary-text font-bold">
                    <th className="py-3 px-4">{isHi ? 'रैंक / नाम' : 'Rank / Candidate Name'}</th>
                    <th className="py-3 px-4">{isHi ? 'अंक (Compound / Single)' : 'Number (Compound / Single)'}</th>
                    <th className="py-3 px-4">{isHi ? 'उद्देश्य अनुकूलता' : 'Purpose Fit'}</th>
                    <th className="py-3 px-4">{isHi ? 'ब्रांड उपयुक्तता' : 'Brand Suitability'}</th>
                    <th className="py-3 px-4">{isHi ? 'कुंडली तालमेल' : 'Astrological Fit'}</th>
                    <th className="py-3 px-4 text-center">{isHi ? 'समग्र स्कोर' : 'Overall Rating'}</th>
                    <th className="py-3 px-4 text-right">{isHi ? 'कार्य' : 'Action'}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-astro-border/70 text-astro-text">
                  {comparisonResult.candidates.map((cand, idx) => {
                    const isWinner = idx === 0;
                    return (
                      <tr
                        key={cand.id}
                        className={`hover:bg-astro-card/70 transition-colors ${
                          isWinner ? 'bg-astro-secondary-surface font-medium' : ''
                        }`}
                      >
                        {/* Name & Rank */}
                        <td className="py-3.5 px-4">
                          <div className="flex items-center gap-2.5">
                            <span
                              className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold shrink-0 ${
                                idx === 0
                                  ? 'bg-amber-400 text-[#263633] shadow-2xs'
                                  : idx === 1
                                  ? 'bg-astro-card text-astro-text'
                                  : idx === 2
                                  ? 'bg-amber-700/20 text-astro-secondary'
                                  : 'bg-astro-secondary-surface text-astro-secondary-text'
                              }`}
                            >
                              {idx + 1}
                            </span>
                            <div>
                              <div className="font-bold text-astro-primary flex items-center gap-1.5">
                                <span>{cand.brandName}</span>
                                {isWinner && (
                                  <span className="px-1.5 py-0.2 rounded text-[9px] font-extrabold bg-astro-secondary/20 text-astro-secondary">
                                    TOP
                                  </span>
                                )}
                              </div>
                              {cand.isDomain && (
                                <div className="text-[10px] text-astro-secondary-text">
                                  Domain: {cand.domainString}{cand.domainExtension}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>

                        {/* Numbers */}
                        <td className="py-3.5 px-4">
                          <div className="flex items-baseline gap-1.5">
                            <span className="font-extrabold text-sm text-astro-primary">
                              {cand.chaldeanCompound}
                            </span>
                            <span className="text-astro-secondary-text">→</span>
                            <span className="font-bold text-xs text-astro-secondary">
                              {cand.chaldeanSingle}
                            </span>
                          </div>
                          <div className="text-[10px] text-astro-secondary-text line-clamp-1">
                            {cand.chaldeanCompoundInfo.symbolName}
                          </div>
                        </td>

                        {/* Purpose Fit */}
                        <td className="py-3.5 px-4">
                          <span
                            className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                              cand.compatibilityWithPurpose === 'EXCELLENT'
                                ? 'bg-emerald-100 text-emerald-800'
                                : cand.compatibilityWithPurpose === 'HIGH'
                                ? 'bg-teal-100 text-teal-800'
                                : cand.compatibilityWithPurpose === 'MODERATE'
                                ? 'bg-astro-secondary/15 text-astro-secondary'
                                : 'bg-rose-100 text-rose-800'
                            }`}
                          >
                            {cand.compatibilityWithPurpose}
                          </span>
                        </td>

                        {/* Brand Suitability */}
                        <td className="py-3.5 px-4">
                          <span
                            className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                              cand.brandSuitability === 'EXCELLENT'
                                ? 'bg-emerald-100 text-emerald-800'
                                : cand.brandSuitability === 'GOOD'
                                ? 'bg-teal-100 text-teal-800'
                                : cand.brandSuitability === 'MODERATE'
                                ? 'bg-astro-secondary/15 text-astro-secondary'
                                : 'bg-rose-100 text-rose-800'
                            }`}
                          >
                            {cand.brandSuitability}
                          </span>
                          <div className="text-[10px] text-astro-secondary-text mt-0.5">
                            {cand.brandAnalytics.syllableCount} syllables
                          </div>
                        </td>

                        {/* Astrological Fit */}
                        <td className="py-3.5 px-4">
                          <span
                            className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                              cand.compatibilityWithKundali === 'HIGH'
                                ? 'bg-emerald-100 text-emerald-800'
                                : cand.compatibilityWithKundali === 'MODERATE'
                                ? 'bg-astro-secondary/15 text-astro-secondary'
                                : cand.compatibilityWithKundali === 'LOW'
                                ? 'bg-rose-100 text-rose-800'
                                : 'bg-astro-secondary-surface text-astro-secondary-text'
                            }`}
                          >
                            {cand.compatibilityWithKundali === 'NOT_APPLICABLE' ? 'Universal' : cand.compatibilityWithKundali}
                          </span>
                        </td>

                        {/* Overall Stars & Score */}
                        <td className="py-3.5 px-4 text-center">
                          <div className="flex flex-col items-center gap-0.5">
                            {renderStars(cand.starRating)}
                            <span className="text-[11px] font-extrabold text-astro-primary">
                              {cand.overallScore}/100
                            </span>
                          </div>
                        </td>

                        {/* Action */}
                        <td className="py-3.5 px-4 text-right">
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedCandidateIndex(idx);
                              setActiveTab('deep-dive');
                            }}
                            className="px-2.5 py-1 rounded-lg bg-astro-card hover:bg-astro-border text-astro-primary font-semibold text-[11px] transition-colors"
                          >
                            {isHi ? 'विवरण' : 'Inspect'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* VIEW 2: CANDIDATE DEEP-DIVE */}
      {activeTab === 'deep-dive' && (
        <div className="space-y-6">
          {/* Candidate Selector Tabs */}
          <div className="flex items-center gap-2 overflow-x-auto pb-1">
            {comparisonResult.candidates.map((cand, idx) => (
              <button
                key={cand.id}
                onClick={() => setSelectedCandidateIndex(idx)}
                className={`px-3.5 py-2 rounded-xl text-xs font-bold whitespace-nowrap transition-all flex items-center gap-2 ${
                  selectedCandidateIndex === idx
                    ? 'bg-astro-primary text-astro-bg shadow-sm'
                    : 'bg-astro-bg border border-astro-border text-astro-secondary-text hover:bg-astro-card'
                }`}
              >
                <span className="w-5 h-5 rounded-full bg-black/10 flex items-center justify-center text-[10px]">
                  {idx + 1}
                </span>
                <span>{cand.brandName}</span>
                <span className="text-[10px] opacity-80">({cand.overallScore} pts)</span>
              </button>
            ))}
          </div>

          {/* Selected Candidate Detailed Inspection */}
          {comparisonResult.candidates[selectedCandidateIndex] && (() => {
            const cand = comparisonResult.candidates[selectedCandidateIndex];
            return (
              <div className="space-y-6">
                {/* Header Card */}
                <div className="bg-astro-bg border border-astro-border rounded-2xl p-6 shadow-sm">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-astro-border pb-5">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-astro-primary/10 text-astro-primary">
                          {isHi ? 'उम्मीदवार विश्लेषण' : 'Candidate Evaluation'}
                        </span>
                        <span className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-astro-secondary/15 text-astro-secondary">
                          {cand.brandSuitability} Suitability
                        </span>
                      </div>
                      <h3 className="text-2xl font-bold font-serif text-astro-primary">
                        {cand.brandName}
                      </h3>
                      {cand.isDomain && (
                        <p className="text-xs text-astro-secondary-text mt-0.5">
                          Domain Decomposition: <strong>{cand.brandName}</strong> (Brand) + <strong>{cand.domainString}</strong> (String) + <strong>{cand.domainExtension}</strong> (Extension)
                        </p>
                      )}
                    </div>

                    <div className="flex items-center gap-4">
                      <div className="text-center bg-astro-card px-4 py-2 rounded-xl border border-astro-border">
                        <div className="text-[10px] font-bold text-astro-secondary-text uppercase">Overall Score</div>
                        <div className="text-2xl font-black text-astro-primary">{cand.overallScore}/100</div>
                        <div className="mt-0.5">{renderStars(cand.starRating)}</div>
                      </div>
                    </div>
                  </div>

                  {/* Letter Breakdown Grid */}
                  <div className="mt-5 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-astro-primary uppercase tracking-wider">
                        {isHi ? `${system} वर्णमाला अंक गणना` : `${system} Letter-by-Letter Calculation`}
                      </span>
                      <span className="text-xs font-semibold text-astro-secondary-text">
                        Sum: <strong className="text-astro-primary">{cand.chaldeanCompound}</strong> → Single: <strong className="text-astro-secondary">{cand.chaldeanSingle}</strong>
                      </span>
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                      {cand.chaldeanBreakdown.map((item, bIdx) => (
                        <div
                          key={bIdx}
                          className="flex flex-col items-center bg-astro-card border border-astro-border rounded-xl px-3 py-2 min-w-[42px] shadow-2xs"
                        >
                          <span className="text-sm font-extrabold font-mono text-astro-primary">
                            {item.char}
                          </span>
                          <span className="text-[11px] font-bold text-astro-secondary">
                            {item.value}
                          </span>
                        </div>
                      ))}
                      <div className="flex items-center font-bold text-lg text-astro-primary px-2">
                        =
                      </div>
                      <div className="flex flex-col items-center bg-astro-primary text-astro-bg rounded-xl px-4 py-2 shadow-2xs">
                        <span className="text-sm font-extrabold">{cand.chaldeanCompound}</span>
                        <span className="text-[10px] text-astro-bg/80">Compound</span>
                      </div>
                      <div className="flex items-center font-bold text-lg text-astro-primary px-2">
                        →
                      </div>
                      <div className="flex flex-col items-center bg-astro-secondary-surface0 text-white rounded-xl px-4 py-2 shadow-2xs">
                        <span className="text-sm font-extrabold">{cand.chaldeanSingle}</span>
                        <span className="text-[10px] text-white/90">Single Digit</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 3 Pillars Comparison Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Pillar 1: Compound Number Symbolism */}
                  <div className="bg-astro-bg border border-astro-border rounded-2xl p-5 shadow-sm space-y-3">
                    <div className="flex items-center gap-2 text-[#52635F] font-bold text-xs uppercase tracking-wider">
                      <Sparkles className="w-4 h-4" />
                      <span>{isHi ? 'संयुक्त अंक रहस्य' : 'Compound Number Vibration'}</span>
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-3xl font-serif font-black text-astro-primary">
                        {cand.chaldeanCompound}
                      </span>
                      <span className="text-xs font-bold text-astro-secondary">
                        {cand.chaldeanCompoundInfo.symbolName}
                      </span>
                    </div>
                    <p className="text-xs text-astro-text leading-relaxed">
                      {isHi ? cand.chaldeanCompoundInfo.meaningHi : cand.chaldeanCompoundInfo.meaning}
                    </p>
                    <div className="pt-2 border-t border-astro-border text-[11px] text-astro-secondary-text">
                      <strong>{isHi ? 'वाणिज्यिक प्रभाव:' : 'Commercial Suitability:'}</strong>{' '}
                      {isHi ? cand.chaldeanCompoundInfo.commercialSuitabilityHi : cand.chaldeanCompoundInfo.commercialSuitability}
                    </div>
                  </div>

                  {/* Pillar 2: Brand / Practical Linguistics */}
                  <div className="bg-astro-bg border border-astro-border rounded-2xl p-5 shadow-sm space-y-3">
                    <div className="flex items-center gap-2 text-astro-primary font-bold text-xs uppercase tracking-wider">
                      <Globe className="w-4 h-4" />
                      <span>{isHi ? 'व्यावहारिक ब्रांड विश्लेषण' : 'Brand & Practical Analysis'}</span>
                    </div>
                    <div className="space-y-2 text-xs">
                      <div className="flex items-center justify-between">
                        <span className="text-astro-secondary-text">{isHi ? 'उच्चारण सुगमता:' : 'Pronunciation Ease:'}</span>
                        <strong className="text-astro-primary">{cand.brandAnalytics.pronunciationScore}/100</strong>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-astro-secondary-text">{isHi ? 'स्मरण क्षमता (Memorability):' : 'Memorability:'}</span>
                        <strong className="text-astro-primary">{cand.brandAnalytics.memorabilityScore}/100</strong>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-astro-secondary-text">{isHi ? 'सर्च व वर्तनी सरलता:' : 'Spelling & Search:'}</span>
                        <strong className="text-astro-primary">{cand.brandAnalytics.spellingSimplicityScore}/100</strong>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-astro-secondary-text">{isHi ? 'ज्योतिष प्रासंगिकता:' : 'Astrology Resonance:'}</span>
                        <span className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-astro-secondary-surface text-astro-secondary">
                          {cand.brandAnalytics.astrologyRelevance}
                        </span>
                      </div>
                    </div>
                    <p className="text-[11px] text-astro-secondary-text pt-2 border-t border-astro-border leading-relaxed">
                      {isHi ? cand.brandAnalytics.phoneticSummaryHi : cand.brandAnalytics.phoneticSummary}
                    </p>
                  </div>

                  {/* Pillar 3: Astrological Synergy */}
                  <div className="bg-astro-bg border border-astro-border rounded-2xl p-5 shadow-sm space-y-3">
                    <div className="flex items-center gap-2 text-teal-800 font-bold text-xs uppercase tracking-wider">
                      <ShieldCheck className="w-4 h-4" />
                      <span>{isHi ? 'ज्योतिषीय कुंडली सामंजस्य' : 'Astrological Kundali Fit'}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-astro-secondary-text">{isHi ? 'कुंडली अनुकूलता:' : 'Chart Synergy:'}</span>
                      <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-teal-100 text-teal-900">
                        {cand.compatibilityWithKundali}
                      </span>
                    </div>
                    <div className="space-y-1.5 text-xs text-astro-text">
                      <div className="bg-astro-card p-2.5 rounded-xl border border-astro-border text-[11px]">
                        <strong>{isHi ? 'अंक ज्योतिषीय संकेत:' : 'Numerological Indication:'}</strong> Single Number {cand.chaldeanSingle} connects with ruling vibration.
                      </div>
                      <div className="bg-teal-50/60 p-2.5 rounded-xl border border-teal-200 text-[11px] text-teal-950">
                        <strong>{isHi ? 'शास्त्रीय ज्योतिषीय संकेत:' : 'Astrological Indication:'}</strong> {isHi ? cand.kundaliSynergyNotesHi : cand.kundaliSynergyNotes}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* VIEW 3: NAME CHANGE EVALUATION */}
      {activeTab === 'name-change' && (
        <div className="space-y-6">
          <div className="bg-astro-bg border border-astro-border rounded-2xl p-6 shadow-sm space-y-5">
            <div>
              <h4 className="text-lg font-bold font-serif text-astro-primary">
                {isHi ? 'नाम या ब्रांड परिवर्तन मूल्यांकन' : 'Name Change & Brand Migration Advisor'}
              </h4>
              <p className="text-xs text-astro-secondary-text mt-0.5">
                {isHi
                  ? 'वर्तमान नाम और प्रस्तावित नए नाम की तुलना करें। देखें कि क्या नाम बदलना अंक ज्योतिषीय व व्यावहारिक दृष्टि से लाभकारी है।'
                  : 'Compare your existing name against proposed alternatives to evaluate vibrational upgrades and practical transition impact.'}
              </p>
            </div>

            {/* Inputs: Current vs Proposed */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-astro-secondary-text uppercase">
                  {isHi ? 'वर्तमान नाम (Current Name)' : 'Current Name'}
                </label>
                <input
                  type="text"
                  value={currentName}
                  onChange={(e) => setCurrentName(e.target.value)}
                  className="w-full bg-astro-card border border-astro-border rounded-xl px-4 py-2.5 text-xs font-bold text-astro-primary focus:ring-1 focus:ring-astro-primary"
                  placeholder="e.g. Vedang Jyotish"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold text-astro-secondary-text uppercase">
                  {isHi ? 'प्रस्तावित नया नाम (Proposed Name)' : 'Proposed Name'}
                </label>
                <input
                  type="text"
                  value={proposedName}
                  onChange={(e) => setProposedName(e.target.value)}
                  className="w-full bg-astro-card border border-astro-border rounded-xl px-4 py-2.5 text-xs font-bold text-astro-primary focus:ring-1 focus:ring-astro-primary"
                  placeholder="e.g. Vedanga Jyotisha"
                />
              </div>
            </div>

            {/* Comparison Delta Banner */}
            <div
              className={`p-5 rounded-2xl border ${
                nameChangeResult.isFavorableShift
                  ? 'bg-emerald-50/70 border-emerald-300 text-emerald-950'
                  : 'bg-astro-secondary-surface/70 border-[#E2DED5] text-[#263633]'
              }`}
            >
              <div className="flex items-start gap-3.5">
                <div
                  className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold text-white shrink-0 ${
                    nameChangeResult.isFavorableShift ? 'bg-emerald-600' : 'bg-astro-secondary'
                  }`}
                >
                  {nameChangeResult.isFavorableShift ? '✓' : '!'}
                </div>
                <div>
                  <div className="text-xs font-bold uppercase tracking-wider">
                    {nameChangeResult.isFavorableShift
                      ? (isHi ? 'अनुकूल व शुभ परिवर्तन' : 'FAVOURABLE MIGRATION')
                      : (isHi ? 'वर्तमान नाम बनाए रखना बेहतर' : 'RETAINING CURRENT IS PREFERABLE')}
                  </div>
                  <h5 className="text-base font-bold font-serif mt-0.5">
                    {isHi ? nameChangeResult.summaryHi : nameChangeResult.summary}
                  </h5>
                  <div className="mt-2 text-xs font-semibold">
                    Score Shift: {nameChangeResult.currentCandidate.overallScore} → {nameChangeResult.proposedCandidate.overallScore} ({nameChangeResult.scoreDifference > 0 ? `+${nameChangeResult.scoreDifference}` : nameChangeResult.scoreDifference} pts)
                  </div>
                </div>
              </div>
            </div>

            {/* Key Benefits vs Transition Risks */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-astro-card p-4 rounded-xl border border-astro-border space-y-2">
                <span className="text-xs font-bold text-emerald-800 uppercase flex items-center gap-1.5">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  <span>{isHi ? 'परिवर्तन के मुख्य लाभ:' : 'Key Upgrades / Benefits:'}</span>
                </span>
                <ul className="space-y-1.5 text-xs text-astro-text">
                  {(isHi ? nameChangeResult.keyBenefitsHi : nameChangeResult.keyBenefits).map((b, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <span className="text-emerald-700 font-bold">•</span>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-astro-card p-4 rounded-xl border border-astro-border space-y-2">
                <span className="text-xs font-bold text-astro-secondary uppercase flex items-center gap-1.5">
                  <AlertCircle className="w-4 h-4 text-amber-600" />
                  <span>{isHi ? 'व्यावहारिक ध्यान देने योग्य बातें:' : 'Practical Considerations:'}</span>
                </span>
                <ul className="space-y-1.5 text-xs text-astro-text">
                  {(isHi ? nameChangeResult.risksHi : nameChangeResult.risks).map((r, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <span className="text-[#52635F] font-bold">•</span>
                      <span>{r}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* VIEW 4: MUHURTA + REGISTRATION TIMING */}
      {activeTab === 'muhurta' && (
        <div className="space-y-6">
          <div className="bg-astro-bg border border-astro-border rounded-2xl p-6 shadow-sm space-y-5">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-astro-border pb-4">
              <div>
                <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-astro-secondary/15 text-astro-secondary uppercase">
                  {isHi ? 'शुभ मुहूर्त चयन' : 'Auspicious Registration Muhurta'}
                </span>
                <h4 className="text-lg font-bold font-serif text-astro-primary mt-1">
                  {isHi
                    ? `"${comparisonResult.bestOverall.brandName}" के पंजीकरण व शुभारंभ का मुहूर्त`
                    : `Muhurta Windows for "${comparisonResult.bestOverall.brandName}"`}
                </h4>
                <p className="text-xs text-astro-secondary-text">
                  {isHi
                    ? 'डोमेन क्रय, ट्रेडमार्क आवेदन, ऐप लॉन्च व व्यावसायिक प्रतिष्ठान के लिए वैदिक मुहूर्त मार्गदर्शन।'
                    : 'Recommended Vedic astrological timing for domain acquisition, trademark filing, and app release.'}
                </p>
              </div>

              <div className="text-right shrink-0">
                <div className="text-[10px] text-astro-secondary-text uppercase font-bold">Selected Name</div>
                <div className="text-base font-bold text-astro-primary">{comparisonResult.bestOverall.brandName}</div>
              </div>
            </div>

            {bestCandidateMuhurta && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Days */}
                <div className="bg-astro-card p-4 rounded-xl border border-astro-border">
                  <div className="text-[11px] font-bold text-astro-secondary-text uppercase">
                    {isHi ? 'शुभ वार (Days)' : 'Favorable Days'}
                  </div>
                  <div className="text-sm font-extrabold text-astro-primary mt-1.5">
                    {(isHi ? bestCandidateMuhurta.recommendedDaysHi : bestCandidateMuhurta.recommendedDays).join(', ')}
                  </div>
                </div>

                {/* Tithis */}
                <div className="bg-astro-card p-4 rounded-xl border border-astro-border">
                  <div className="text-[11px] font-bold text-astro-secondary-text uppercase">
                    {isHi ? 'शुभ तिथियां (Tithis)' : 'Auspicious Tithis'}
                  </div>
                  <div className="text-xs font-bold text-astro-primary mt-1.5 line-clamp-2">
                    {(isHi ? bestCandidateMuhurta.favorableTithisHi : bestCandidateMuhurta.favorableTithis).join(', ')}
                  </div>
                </div>

                {/* Nakshatras */}
                <div className="bg-astro-card p-4 rounded-xl border border-astro-border">
                  <div className="text-[11px] font-bold text-astro-secondary-text uppercase">
                    {isHi ? 'शुभ नक्षत्र (Nakshatras)' : 'Best Nakshatras'}
                  </div>
                  <div className="text-xs font-bold text-astro-primary mt-1.5 line-clamp-2">
                    {(isHi ? bestCandidateMuhurta.favorableNakshatrasHi : bestCandidateMuhurta.favorableNakshatras).join(', ')}
                  </div>
                </div>

                {/* Hora */}
                <div className="bg-astro-card p-4 rounded-xl border border-astro-border">
                  <div className="text-[11px] font-bold text-astro-secondary-text uppercase">
                    {isHi ? 'शुभ होरा (Hora)' : 'Auspicious Hora'}
                  </div>
                  <div className="text-xs font-bold text-astro-secondary mt-1.5">
                    {bestCandidateMuhurta.auspiciousHoraLords.join(', ')}
                  </div>
                </div>
              </div>
            )}

            {/* Practical Guidelines Banner */}
            <div className="bg-astro-secondary-surface/60 border border-[#E2DED5] p-4 rounded-xl text-xs text-[#263633] flex items-start gap-3">
              <Info className="w-4 h-4 text-[#52635F] shrink-0 mt-0.5" />
              <div>
                <strong className="font-bold">{isHi ? 'वैदिक परामर्श निर्देश:' : 'Vedic Muhurta Guidelines:'}</strong>{' '}
                {isHi ? bestCandidateMuhurta?.guidelinesHi : bestCandidateMuhurta?.guidelines}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Advisory Principles Footer Banner (Section 84 Compliance) */}
      <div className="bg-astro-card border border-astro-border rounded-xl p-4 text-[11px] text-astro-secondary-text flex items-start gap-2.5">
        <Info className="w-4 h-4 text-astro-primary shrink-0 mt-0.5" />
        <div>
          <strong className="text-astro-primary font-bold">
            {isHi ? 'महत्वपूर्ण अंक ज्योतिष सिद्धांत:' : 'Important Advisory Principle:'}
          </strong>{' '}
          {isHi ? comparisonResult.disclaimerHi : comparisonResult.disclaimer}
        </div>
      </div>
    </div>
  );
};
